import {
  IDocumentAssemblerTemplateCategory,
  useDocumentAssemblerTemplateCategories,
} from '@commenda/api';
import {
  Button,
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import { pluralize } from '@commenda/utils';
import { useMemo, useState } from 'react';

import { DocumentAssemblerCategoryCreateDialog } from './DocumentAssemblerCategoryCreateDialog';

interface DocumentAssemblerCategoryPickerProps {
  /** The selected category's key, or '' for none. A template stores keys; so does a workflow step's scope. */
  selectedCategoryKey: string;
  onSelect: (category: IDocumentAssemblerTemplateCategory | null) => void;
  label: string;
  hint?: string;
  isRequired?: boolean;
  error?: string;
  disabled?: boolean;
  isInsideDialog?: boolean;
  canCreate?: boolean;
  /** Gates the category fetch only — it is not the inverse of `disabled`. */
  shouldFetchCategories?: boolean;
}

export const DocumentAssemblerCategoryPicker = ({
  selectedCategoryKey,
  onSelect,
  label,
  hint,
  isRequired = false,
  error,
  disabled = false,
  isInsideDialog = false,
  canCreate = false,
  shouldFetchCategories = true,
}: DocumentAssemblerCategoryPickerProps) => {
  const createCategoryModalVisibility = useToggle(false);
  const [prefilledName, setPrefilledName] = useState('');

  const {
    templateCategories,
    error: listError,
    isLoading,
    mutate,
  } = useDocumentAssemblerTemplateCategories({
    enabled: shouldFetchCategories,
  });

  const categories = useMemo(
    () => templateCategories ?? [],
    [templateCategories]
  );

  // An empty `categories` is not proof the selection is gone: `templateCategories` is undefined
  // while the first fetch is in flight, and stays undefined forever when `shouldFetchCategories` is
  // false — the SWR key is null then, so `isLoading` never turns true either. Only a list that has
  // actually settled (data arrived, or the fetch failed) can be used to judge the selection.
  const hasResolvedCategoryList =
    !isLoading && (Boolean(listError) || templateCategories !== undefined);

  const options = useMemo(() => {
    const existingCategoryOptions = categories.map((category) => ({
      value: category.key,
      label: `${category.name} (${pluralize(category.templateCount, 'template')})`,
    }));

    if (
      hasResolvedCategoryList &&
      selectedCategoryKey &&
      !existingCategoryOptions.some(
        (option) => option.value === selectedCategoryKey
      )
    ) {
      return [
        ...existingCategoryOptions,
        {
          value: selectedCategoryKey,
          label: `${selectedCategoryKey} — not available`,
        },
      ];
    }

    return existingCategoryOptions;
  }, [categories, hasResolvedCategoryList, selectedCategoryKey]);

  const isStranded =
    hasResolvedCategoryList &&
    Boolean(selectedCategoryKey) &&
    !categories.some((category) => category.key === selectedCategoryKey);

  const strandedMessage = listError
    ? // An unloadable list must never accuse a valid value of being wrong.
      'The categories could not be loaded, so this cannot be checked.'
    : 'No category with this key exists any more, so this template would not be offered where that family is in scope.';

  return (
    <FieldWrapper>
      <Label required={isRequired} optional={!isRequired}>
        {label}
      </Label>

      <SingleCombobox
        options={options}
        value={selectedCategoryKey}
        setValue={(next) =>
          onSelect(categories.find((category) => category.key === next) ?? null)
        }
        placeholder={isLoading ? 'Loading categories…' : 'Select a category'}
        searchPlaceholder="Search categories"
        notFoundText="No categories exist yet."
        disabled={disabled || isLoading}
        modalPopover={isInsideDialog}
        error={error}
        addItemConfig={
          canCreate
            ? {
                shouldAllowAdd: true,
                createFromSearch: true,
                placeholder: 'Add a category',
                onAddItem: (searchText) => {
                  setPrefilledName(searchText);
                  createCategoryModalVisibility.on();
                },
              }
            : undefined
        }
      />

      {/* SingleCombobox takes `error` but renders it only as a border colour. */}
      {error ? <InputErrorMessage message={error} /> : null}

      {listError ? (
        <div className="flex items-center gap-sm">
          <InputErrorMessage message="The categories could not be loaded." />
          <Button variant="tertiary" size="xs" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      ) : null}

      {isStranded && !error ? (
        <InputErrorMessage message={strandedMessage} />
      ) : null}

      {hint ? (
        <p className="txt-extra-small-regular mt-xs text-text-secondary">
          {hint}
        </p>
      ) : null}

      {createCategoryModalVisibility.value ? (
        <DocumentAssemblerCategoryCreateDialog
          initialName={prefilledName}
          onClose={createCategoryModalVisibility.off}
          onCreated={async (category) => {
            await mutate();
            onSelect(category);
          }}
        />
      ) : null}
    </FieldWrapper>
  );
};
